/**
 * Reusable Button component with variants
 * Patient Survey SPA
 */

import React from 'react';
import styles from './Button.module.css';

/**
 * Button variant types
 */
export type ButtonVariant = 'primary' | 'secondary' | 'outline';

/**
 * Button size types
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Button component
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant of the button */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Button content */
  children: React.ReactNode;
}

/**
 * Reusable Button component with support for variants, sizes, and states
 *
 * @example
 * // Primary button (default)
 * <Button onClick={handleClick}>Submit</Button>
 *
 * @example
 * // Secondary outline button
 * <Button variant="outline" size="sm">Cancel</Button>
 *
 * @example
 * // Disabled button
 * <Button disabled>Disabled</Button>
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  type = 'button',
  className,
  children,
  ...props
}) => {
  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : '',
    loading ? styles.loading : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      {...props}
    >
      {loading && (
        <span className={styles.spinner} aria-hidden="true">
          <svg
            className={styles.spinnerIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className={styles.spinnerCircle}
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
            />
          </svg>
        </span>
      )}
      <span className={loading ? styles.contentHidden : styles.content}>
        {children}
      </span>
      {loading && <span className="sr-only">Loading...</span>}
    </button>
  );
};

export default Button;
